import { CalendarDays, ChevronDown, ChevronRight, Layers, Search, X } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/controls'
import { Input } from '@/components/ui/field'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlays'
import { MAX_CONTEXT_MEETINGS, type ChatScope } from '@/data/chat'
import { findMeeting, selectableCount, selectableMeetings } from '@/data/meetings'
import { findSpace } from '@/data/spaces'
import { DatePicker } from '@/features/meetings/DatePicker'
import { PeopleFilter, SpaceFilter } from '@/features/meetings/filters'
import type { Meeting, MeetingFilters } from '@/data/types'
import { cn } from '@/lib/utils'

export type Scope = ChatScope

const NO_FILTERS: MeetingFilters = {
  query: '',
  people: { participant: [], access: [], shared: [] },
  spaces: [],
}

/** Enough rows to scroll through without loading the whole corpus into a popover. */
const LIST_LIMIT = 80

function resolve(ids: string[]): Meeting[] {
  return ids
    .map((id) => findMeeting(id))
    .filter((meeting): meeting is Meeting => meeting !== undefined)
}

const DAY = 24 * 60 * 60 * 1000

/** Today and Yesterday by name; anything older by date. */
function dayHeading(iso: string): string {
  const start = new Date(iso)
  start.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Math.round((today.getTime() - start.getTime()) / DAY)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return start.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * What this conversation reads.
 *
 * The panel is the Meetings screen's toolbar over the Meetings screen's list,
 * inside the composer: the same search field, the same jump-to-date, the same
 * People and Spaces dropdowns, running through the same `MeetingFilters` and the
 * same `matches` in the data layer. A picker with a private idea of "shared with
 * Rachel" would be a second answer to a question that screen already answers,
 * and the two would drift on the first change to either.
 *
 * Filtering and scoping are then two separate acts, which is the whole point:
 * the filters find candidates, and TICKING is what loads them into the context.
 * Tick nothing and the space filter stands as the scope, so "chat inside this
 * deal" is still one click and does not need its own control.
 *
 * The cap is real and stated rather than silently enforced — twenty transcripts
 * is already more than a turn can read closely, and a selection that quietly
 * dropped the twenty-first would answer a narrower question than the one asked.
 */
export function ScopePicker({
  scope,
  onChange,
  disabled,
}: {
  scope: Scope
  onChange: (scope: Scope) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  /**
   * Filters belong to the picker, not to the thread: they are how you FIND the
   * meetings, and once they are ticked the selection is the scope. Keeping them
   * on the thread would mean reopening a chat re-ran a search whose result may
   * since have changed under the answer already on screen.
   */
  const [filters, setFilters] = useState<MeetingFilters>({
    ...NO_FILTERS,
    spaces: scope.spaceIds,
  })
  const [jumpDate, setJumpDate] = useState<string | null>(null)

  const picked = scope.meetingIds
  const pickedMeetings = resolve(picked)
  const scopeSpaces = scope.spaceIds.flatMap((id) => findSpace(id) ?? [])

  const meetings = selectableMeetings(filters, jumpDate, LIST_LIMIT)
  const total = selectableCount(filters, jumpDate)
  const room = MAX_CONTEXT_MEETINGS - picked.length

  const activeFilters =
    filters.people.participant.length +
    filters.people.access.length +
    filters.people.shared.length +
    filters.spaces.length +
    (jumpDate ? 1 : 0) +
    (filters.query.trim() ? 1 : 0)

  function toggle(id: string) {
    if (!picked.includes(id) && room <= 0) return
    onChange({
      spaceIds: filters.spaces,
      meetingIds: picked.includes(id) ? picked.filter((item) => item !== id) : [...picked, id],
    })
  }

  /** Everything the filters match, oldest of the twenty dropped rather than the newest. */
  function selectAll() {
    onChange({
      spaceIds: filters.spaces,
      meetingIds: meetings.slice(0, MAX_CONTEXT_MEETINGS).map((meeting) => meeting.id),
    })
  }

  function clearSelection() {
    onChange({ spaceIds: filters.spaces, meetingIds: [] })
  }

  function setSpaces(spaces: string[]) {
    // The space filter doubles as the scope while nothing is ticked, so it is
    // written through in that case and is only a filter once it is not.
    setFilters((current) => ({ ...current, spaces }))
    if (picked.length === 0) onChange({ spaceIds: spaces, meetingIds: [] })
  }

  const spacesLabel =
    scopeSpaces.length === 0
      ? null
      : scopeSpaces.length === 1
        ? scopeSpaces[0]!.name
        : `${scopeSpaces.length} spaces`

  const summary =
    picked.length > 0
      ? `Reading ${picked.length} selected meeting${picked.length === 1 ? '' : 's'}`
      : spacesLabel
        ? `Searching everything in ${spacesLabel}`
        : 'Searching every meeting'

  const allSelected =
    meetings.length > 0 && meetings.slice(0, MAX_CONTEXT_MEETINGS).every((m) => picked.includes(m.id))

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        // Reopening starts from what the conversation is actually scoped to,
        // not from wherever the filters were left pointing last time.
        if (next) {
          setFilters({ ...NO_FILTERS, spaces: scope.spaceIds })
          setJumpDate(null)
        }
      }}
    >
      <PopoverTrigger
        disabled={disabled}
        aria-label={`Scope: ${summary}. Change it`}
        className={cn(
          'inline-flex h-7 min-w-0 items-center gap-1.5 rounded-[var(--radius-sm)] px-2 text-xs outline-none',
          'transition-colors duration-100 disabled:opacity-40',
          'hover:bg-[var(--color-surface-hover)] data-[popup-open]:bg-[var(--color-surface-hover)]',
          scopeSpaces.length > 0 || picked.length > 0
            ? 'text-[var(--color-fg)]'
            : 'text-[var(--color-fg-muted)]'
        )}
      >
        {picked.length > 0 ? (
          <CalendarDays className="size-3.5 shrink-0" />
        ) : (
          <Layers className="size-3.5 shrink-0" />
        )}
        {/* One meeting is named, with its space as a breadcrumb. Several are
            counted — four titles in a 7px-tall chip is not a label. */}
        {picked.length === 1 && scopeSpaces.length === 1 ? (
          <>
            <span className="shrink-0 font-mono text-2xs text-[var(--color-fg-subtle)]">
              {scopeSpaces[0]!.code}
            </span>
            <ChevronRight className="size-3 shrink-0 text-[var(--color-fg-subtle)]" />
          </>
        ) : null}
        <span className="max-w-48 truncate">
          {picked.length === 1
            ? pickedMeetings[0]!.title
            : picked.length > 1
              ? `${picked.length} meetings`
              : (spacesLabel ?? 'All meetings')}
        </span>
        <ChevronDown className="size-3 shrink-0 opacity-60" />
      </PopoverTrigger>

      <PopoverContent align="start" side="top" className="w-[32rem] max-w-[calc(100vw-2rem)] p-0">
        <div className="relative border-b border-[var(--color-border)]">
          <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
          <Input
            autoFocus
            value={filters.query}
            onChange={(event) =>
              setFilters((current) => ({ ...current, query: event.target.value }))
            }
            placeholder="Search meetings and people…"
            aria-label="Search meetings"
            className="h-9 rounded-none border-0 pl-8 text-xs focus-visible:ring-0"
          />
        </div>

        {/* The meetings toolbar's own three controls, not lookalikes.

            Stretched to fill the row rather than left as three natural-width
            buttons: in a toolbar they sit beside a search field that eats the
            slack, and lifted into a fixed-width panel that slack becomes a bare
            third of a row with nothing in it. Each trigger is the Popover's
            direct DOM child, so the width comes from here and the controls stay
            untouched for the toolbar that renders them at their own size. */}
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-2 py-2">
          <div className="grid flex-1 grid-cols-3 gap-2 [&>button]:w-full [&>button]:justify-center">
            <DatePicker
              domain="past"
              value={jumpDate}
              onSelect={setJumpDate}
              onClear={() => setJumpDate(null)}
            />
            <PeopleFilter
              people={filters.people}
              onChange={(people) => setFilters((current) => ({ ...current, people }))}
            />
            <SpaceFilter
              selected={filters.spaces}
              onToggle={(id) =>
                setSpaces(
                  filters.spaces.includes(id)
                    ? filters.spaces.filter((item) => item !== id)
                    : [...filters.spaces, id]
                )
              }
            />
          </div>
          {activeFilters > 0 ? (
            <button
              type="button"
              onClick={() => {
                setJumpDate(null)
                setFilters(NO_FILTERS)
                if (picked.length === 0) onChange({ spaceIds: [], meetingIds: [] })
              }}
              className="inline-flex h-8 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-2xs text-[var(--color-fg-subtle)] outline-none hover:text-[var(--color-fg)]"
            >
              <X className="size-3" />
              Clear filters
            </button>
          ) : null}
        </div>

        {/* Take the filtered set wholesale, or read down it and tick. */}
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-2 py-1.5">
          <button
            type="button"
            onClick={() => (allSelected ? clearSelection() : selectAll())}
            disabled={meetings.length === 0}
            className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] px-1 py-0.5 text-2xs text-[var(--color-fg)] outline-none hover:bg-[var(--color-surface-hover)] disabled:opacity-40"
          >
            <span className="pointer-events-none">
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && picked.length > 0}
                onCheckedChange={() => {}}
                aria-label=""
              />
            </span>
            {allSelected ? 'Clear selection' : 'Select all'}
          </button>
          <p className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-subtle)]">
            {total} meeting{total === 1 ? '' : 's'} match
            {total > MAX_CONTEXT_MEETINGS
              ? ` · newest ${MAX_CONTEXT_MEETINGS} on Select all`
              : ''}
          </p>
          <span
            className={cn(
              'tnum shrink-0 text-2xs',
              room <= 0 ? 'text-[var(--color-warning)]' : 'text-[var(--color-fg-subtle)]'
            )}
          >
            {picked.length}/{MAX_CONTEXT_MEETINGS}
          </span>
        </div>

        <div className="max-h-72 overflow-y-auto p-1">
          {meetings.map((meeting, index) => {
            const heading = dayHeading(meeting.startsAt)
            const first = index === 0 || dayHeading(meetings[index - 1]!.startsAt) !== heading
            const space = findSpace(meeting.spaceId)
            const checked = picked.includes(meeting.id)
            return (
              <div key={meeting.id}>
                {first ? (
                  <p className="px-2 pt-2 pb-1 text-2xs font-medium text-[var(--color-fg-subtle)]">
                    {heading}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => toggle(meeting.id)}
                  aria-pressed={checked}
                  aria-label={`${meeting.title}, ${heading} ${timeLabel(meeting.startsAt)}`}
                  disabled={!checked && room <= 0}
                  title={!checked && room <= 0 ? `${MAX_CONTEXT_MEETINGS} is the limit` : undefined}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left',
                    'hover:bg-[var(--color-surface-hover)] disabled:opacity-40 disabled:hover:bg-transparent',
                    checked && 'bg-[var(--color-surface-hover)]'
                  )}
                >
                  {/* Presentational: the row is the control, so the checkbox
                      must not be a second tab stop that toggles the same thing. */}
                  <span className="pointer-events-none">
                    <Checkbox checked={checked} onCheckedChange={() => {}} aria-label="" />
                  </span>
                  <span className="w-9 shrink-0 font-mono text-2xs text-[var(--color-fg-subtle)]">
                    {space?.code ?? ''}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-[var(--color-fg)]">
                      {meeting.title}
                    </span>
                    <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                      {timeLabel(meeting.startsAt)} · {meeting.noteCount} note
                      {meeting.noteCount === 1 ? '' : 's'}
                      {meeting.participants.length > 0
                        ? ` · ${meeting.participants.map((person) => person.name.split(' ')[0]).join(', ')}`
                        : ''}
                    </span>
                  </span>
                </button>
              </div>
            )
          })}
          {meetings.length === 0 ? (
            <p className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
              Nothing matches those filters.
            </p>
          ) : null}
        </div>

        {/* Which of the two won, in words. A set of ticks and a filled-in filter
            each look like an answer; only one of them is. */}
        <div className="flex items-center gap-2 border-t border-[var(--color-border)] px-2 py-1.5">
          <p className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-muted)]">{summary}</p>
          {picked.length > 0 ? (
            <button
              type="button"
              onClick={clearSelection}
              className="rounded-[var(--radius-xs)] px-1.5 py-0.5 text-2xs text-[var(--color-fg-muted)] outline-none hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]"
            >
              Clear
            </button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * How many of the selection to name under the composer.
 *
 * The chips exist so that "which ones" can be checked at a glance before
 * sending. Past a handful they stop doing that and start being a wall — twenty
 * of them took six rows and pushed the composer into the middle of the screen —
 * so the tail is counted and managed in the picker, which is the surface built
 * for it.
 */
const MAX_CHIPS = 6

/**
 * The selection, under the composer.
 *
 * A count in the trigger says how many without saying which, and "which" is
 * exactly what someone re-reads before pressing send. Each chip removes itself,
 * so narrowing a selection never means reopening the picker to find the one row
 * that should not have been ticked.
 */
export function ScopeChips({
  scope,
  onChange,
}: {
  scope: Scope
  onChange: (scope: Scope) => void
}) {
  const all = resolve(scope.meetingIds)
  if (all.length < 2) return null
  const meetings = all.slice(0, MAX_CHIPS)
  const hidden = all.length - meetings.length

  return (
    <div className="flex flex-wrap items-center gap-1">
      {meetings.map((meeting) => {
        const space = findSpace(meeting.spaceId)
        return (
          <span
            key={meeting.id}
            className="inline-flex h-6 max-w-64 items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] pr-1 pl-1.5 text-2xs text-[var(--color-fg-muted)]"
          >
            {space ? (
              <span className="font-mono text-[var(--color-fg-subtle)]">{space.code}</span>
            ) : null}
            <span className="truncate">{meeting.title}</span>
            <button
              type="button"
              aria-label={`Remove ${meeting.title}`}
              onClick={() =>
                onChange({
                  spaceIds: scope.spaceIds,
                  meetingIds: scope.meetingIds.filter((id) => id !== meeting.id),
                })
              }
              className="grid size-4 place-items-center rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] outline-none hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]"
            >
              <X className="size-2.5" />
            </button>
          </span>
        )
      })}
      {hidden > 0 ? (
        <span className="text-2xs text-[var(--color-fg-subtle)]">
          and {hidden} more — open the scope picker to change them
        </span>
      ) : null}
    </div>
  )
}
